import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "What is MITRE ATT&CK?" guide.
 * Accuracy: ATT&CK = Adversarial Tactics, Techniques & Common Knowledge,
 * maintained by MITRE; Enterprise matrix has 14 tactics; tactics (why) →
 * techniques (how) → sub-techniques; technique IDs like T1078.
 */
export function WhatIsMitreAttackGuideBody() {
  return (
    <>
      <Lead>
        MITRE ATT&CK is the common language security teams use to describe how
        attackers behave. It is a free, curated knowledge base of real-world
        adversary tactics and techniques, and it underpins modern detection,
        threat intel and coverage measurement. This guide explains what ATT&CK is,
        how it is structured, and how teams use it.
      </Lead>

      <GuideSection id="what-is-it" title="What is MITRE ATT&CK?">
        <P>
          <Term>MITRE ATT&CK</Term> (Adversarial Tactics, Techniques, and Common
          Knowledge) is a globally-used knowledge base of adversary behaviour,
          maintained by the non-profit MITRE and based on observed real-world attacks.
          Instead of cataloguing malware signatures, it catalogues{' '}
          <Term>behaviour</Term>, what attackers are trying to do and how, which
          makes it durable even as specific tools change.
        </P>
      </GuideSection>

      <GuideSection id="structure" title="Tactics, techniques and sub-techniques">
        <P>The framework is organised in a hierarchy:</P>
        <UL>
          <LI><Term>Tactics</Term>: the attacker&apos;s goal, the &quot;why&quot; of an action (e.g. Initial Access, Persistence, Exfiltration).</LI>
          <LI><Term>Techniques</Term>: the &quot;how&quot;, the method used to achieve a tactic (e.g. Phishing, Valid Accounts, T1078).</LI>
          <LI><Term>Sub-techniques</Term>: more specific variations of a technique.</LI>
          <LI><Term>Procedures</Term>: the specific real-world implementations seen in the wild.</LI>
        </UL>
        <P>
          Behaviours are organised into <Term>matrices</Term>: Enterprise (the most
          used), Mobile, and ICS. The Enterprise matrix has 14 tactics that roughly
          follow an attack&apos;s progression:
        </P>
        <GuideTable
          head={['Stage', 'Example tactics']}
          rows={[
            ['Get in', 'Reconnaissance, Resource Development, Initial Access'],
            ['Run & stay', 'Execution, Persistence, Privilege Escalation, Defense Evasion'],
            ['Operate', 'Credential Access, Discovery, Lateral Movement, Collection'],
            ['Achieve goal', 'Command and Control, Exfiltration, Impact'],
          ]}
        />
      </GuideSection>

      <GuideSection id="how-used" title="How security teams use ATT&CK">
        <UL>
          <LI><Term>Detection engineering</Term>: tag each detection with the technique it catches, creating a shared vocabulary.</LI>
          <LI><Term>Coverage mapping</Term>: build a heatmap of which techniques you can detect and where the gaps are.</LI>
          <LI><Term>Threat intelligence</Term>: describe adversary groups by the techniques they favour.</LI>
          <LI><Term>Red/blue teaming</Term>: plan and measure exercises against a common framework.</LI>
          <LI><Term>Prioritisation</Term>: an attack spanning many tactics is more serious than one noisy technique repeated.</LI>
        </UL>
        <Callout title="Why behaviour beats signatures">
          <p>
            Attackers swap tools constantly, but the underlying techniques change
            slowly. Detecting on behaviour (ATT&CK techniques) is more durable than
            chasing individual indicators.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse uses MITRE ATT&CK">
        <P>
          LogPulse ships 50+ built-in detections, each <Term>tagged with ATT&CK
          tactics and techniques</Term>, and derives a coverage heatmap from your
          enabled content so you can see gaps. Because{' '}
          <GuideLink href="/guides/what-is-risk-based-alerting">risk-based alerting</GuideLink>{' '}
          weights activity that spans more tactics and techniques higher, attack{' '}
          <Term>breadth</Term> across the ATT&CK matrix raises an entity&apos;s risk
          faster than repetitive noise. See{' '}
          <GuideLink href="/guides/what-is-siem">what is SIEM</GuideLink> and{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink>.
        </P>
      </GuideSection>
    </>
  );
}
